import { useEffect, useMemo, useState } from "react";
import { Bills } from "@/lib/api";
import BillCard from "@/components/BillCard";
import { Input } from "@/components/ui/input";
import { Search, AlertTriangle, Clock, CheckCircle2, FileText } from "lucide-react";
import { fmtMoney } from "@/lib/bill-utils";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "overdue", label: "Overdue" },
  { key: "due_soon", label: "Due Soon" },
  { key: "unpaid", label: "Unpaid" },
  { key: "part_paid", label: "Part Paid" },
  { key: "paid", label: "Paid" },
  { key: "emailed", label: "Emailed" },
  { key: "texted", label: "Texted" },
];

const StatCard = ({ label, value, accent = "black", icon: Icon, testid }) => (
  <div data-testid={testid} className="swiss-card">
    <div className="flex items-start justify-between">
      <div className="swiss-label">{label}</div>
      {Icon && <Icon className="h-4 w-4 text-neutral-400" strokeWidth={2} />}
    </div>
    <div
      className="mt-3 font-display font-black text-2xl sm:text-3xl md:text-4xl tracking-tight tabular-nums whitespace-nowrap overflow-hidden text-ellipsis leading-tight"
      style={{ color: accent }}
    >
      {value}
    </div>
  </div>
);

const Dashboard = () => {
  const [bills, setBills] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setLoading(true);
      const [bs, st] = await Promise.all([
        Bills.list({
          search: search || undefined,
          status: status === "all" ? undefined : status,
        }),
        Bills.stats(),
      ]);
      setBills(bs);
      setStats(st);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [status]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const empty = !loading && bills.length === 0;

  const headline = useMemo(() => {
    if (!stats) return "Bills";
    if (stats.total_count === 0) return "Capture your first bill";
    if (stats.overdue_count > 0)
      return `${stats.overdue_count} overdue • Act fast`;
    if (stats.due_soon_count > 0)
      return `${stats.due_soon_count} due soon`;
    return "You're on top of things";
  }, [stats]);

  return (
    <div data-testid="dashboard" className="fade-rise">
      {/* Hero */}
      <section className="mb-10">
        <div className="swiss-label mb-3">Dashboard</div>
        <h1
          data-testid="dashboard-headline"
          className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]"
        >
          {headline}
        </h1>
        <p className="mt-3 text-neutral-600 max-w-2xl">
          Snap a photo, we read it, you review, we remind. Cloud-saved and
          accessible from any device.
        </p>
      </section>

      {/* Stats */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-10">
        <StatCard
          testid="stat-total-owing"
          label="Total Owing"
          value={stats ? fmtMoney(stats.total_owing) : "—"}
          icon={FileText}
        />
        <StatCard
          testid="stat-overdue"
          label="Overdue"
          value={stats ? stats.overdue_count : "—"}
          accent="#FF3B30"
          icon={AlertTriangle}
        />
        <StatCard
          testid="stat-due-soon"
          label="Due Soon"
          value={stats ? stats.due_soon_count : "—"}
          accent="#B45309"
          icon={Clock}
        />
        <StatCard
          testid="stat-paid"
          label="Paid"
          value={stats ? stats.paid_count : "—"}
          accent="#008A00"
          icon={CheckCircle2}
        />
      </section>

      {/* Search + filters */}
      <section className="mb-6">
        <div className="relative mb-4">
          <Search className="h-4 w-4 text-neutral-400 absolute left-0 top-3" />
          <Input
            data-testid="search-input"
            placeholder="Search by company or invoice number"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="swiss-input pl-6"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`filter-${f.key}`}
              onClick={() => setStatus(f.key)}
              className={`px-3 py-2 border-2 text-[11px] uppercase tracking-[0.18em] font-bold transition-colors ${
                status === f.key
                  ? "bg-black text-white border-black"
                  : "bg-white text-neutral-700 border-neutral-300 hover:border-black"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </section>

      {/* List */}
      {loading && (
        <div
          data-testid="loading-state"
          className="text-sm text-neutral-500"
        >
          Loading…
        </div>
      )}

      {empty && (
        <div
          data-testid="empty-state"
          className="swiss-card text-center py-16 border-dashed"
        >
          <div className="swiss-label">No bills yet</div>
          <div className="font-display font-extrabold text-2xl mt-2">
            Capture your first bill
          </div>
          <p className="text-neutral-500 mt-2 max-w-md mx-auto">
            Take a photo of the invoice and we'll pre-fill the details using AI.
            You review, then save.
          </p>
        </div>
      )}

      {!loading && bills.length > 0 && (
        <section
          data-testid="bills-grid"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 stagger"
        >
          {bills.map((b) => (
            <BillCard key={b.id} bill={b} onEmailed={load} />
          ))}
        </section>
      )}
    </div>
  );
};

export default Dashboard;
